import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import { Box, Button } from '@mui/material';

import Spinner from '~/components/Common/Spinner';
import InputRateCodeSelect from '~/components/Cruises/components/Selects/InputRateCodeSelect';

import { useCreateBundleFilter } from '~/hooks/apis/cruises/useBundleFilterMutations';

import cruiseLineService, { CruiseLine } from '~/services/cruises/CruiseLineService';

type Props = {
  bundleId: string;
  vendorId: string;
};

function BundleFilterForm({ bundleId, vendorId }: Props) {
  const history = useHistory();
  const [selectedRateIds, setSelectedRateIds] = useState<Array<string>>([]);
  const [vendor, setVendor] = useState<CruiseLine | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateBundleFilter();

  const loadCruiseLine = useCallback(async () => {
    try {
      const vendor = await cruiseLineService.getCruiseLineById(vendorId);
      setVendor(vendor);
    } catch (err) {
      enqueueSnackbar('Error loading cruise line', { variant: 'error' });
    }
  }, [vendorId, enqueueSnackbar]);

  const handleSubmit = useCallback(async () => {
    createMutation.mutate(
      { bundleId, rateIds: selectedRateIds },
      {
        onSuccess: () => {
          enqueueSnackbar('Bundle filter(s) saved successfully', { variant: 'success' });
          history.push(`/cruises/bundles/edit/${bundleId}/filters`);
        },
        onError: (error) => {
          const errorMessage = error?.message || 'Unknown error';
          enqueueSnackbar(`Error saving bundle filter: ${errorMessage}`, { variant: 'error' });
        },
      },
    );
  }, [selectedRateIds, history, enqueueSnackbar, bundleId, createMutation]);

  const handleCancel = useCallback(() => {
    history.push(`/cruises/bundles/edit/${bundleId}/filters`);
  }, [history, bundleId]);

  const isSaveEnabled = useMemo(() => {
    return !createMutation.isPending && selectedRateIds.length > 0;
  }, [selectedRateIds, createMutation.isPending]);

  useEffect(() => {
    if (!vendor) {
      loadCruiseLine();
    }
  }, [vendor, loadCruiseLine]);

  return (
    <Box>
      <Box mt={2}>
        <InputRateCodeSelect
          vendorCode={vendor?.code}
          selectedRateIds={selectedRateIds}
          isLoading={!vendor}
          onChange={setSelectedRateIds}
        />
      </Box>

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {createMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleFilterForm;
